/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */

// redux utils
import { createSlice, current } from '@reduxjs/toolkit';
// thunks
import {
  fetchAccountSites,
  fetchAccountInfo,
  fetchAccountSitesAndApps,
  fetchAccountSettings,
  updateAccountSettings,
  fetchAccountProducts,
  fetchInvoices,
  fetchInvoice,
  fetchAccountUsers,
  updateAccountDetails,
  fetchHistoricalData,
  fetchInitialRevenueDate,
  fetchAccountCommentingRoles,
  checkCommentingEmailIfExist,
  setCommentingAccountStatus,
  fetchPaymentInformation,
  fetchPaypalInformation,
  fetchACHInformation,
  fetchCheckInformation,
  fetchWireTransferInformation,
  fetchPendingAccountProducts,
  fetchRejectedAccountProducts,
  fetchApprovedAccountProducts,
  updateDisplayInvoice as saveDisplayInvoice, updateEnableApiAccess, updateApiAccess,
} from "../thunks/account";
import { createCustomCommentingRole, updateCustomCommentingRole } from '../thunks/commenting';
import { inviteUser, resendInvite, updateUser, addUser, deleteUser } from '../thunks/users';


/* ========== ~~~~~~~~~~ ACCOUNT SLICE : REDUCER ~~~~~~~~~~ ========== */
const initialState = {
  //sites
  sites: {},
  sitesAndApps: {},
  sitesAndAppsUUIDs: {},
  sitesReceived: false,
  sitesRequestStatus: 'idle',
  sitesAndAppsReceived: false,
  sitesAndAppsRequestStatus: 'idle',
  accountProducts: [],
  accountProductRequestStatus: 'idle',
  accountProductsReceived: false,
  accountName: null,
  businessDomain: null,
  accountGrade: null,
  sellerType: null,
  accountInfoReceived: false,
  accountInfoRequestStatus: 'idle',
  showNewRevenueMetrics: false,
  isOkoAccount: false,
  accountSettings: {},
  accountSettingsRequestStatus: 'idle',
  isLoading: false,
  invoices: [],
  invoicesFromDB: [],
  invoiceUpdateStatus: 'idle',
  accountUsers: [],
  resendingInvite: [],
  sendingInvite: false,
  addingUserError: null,
  addingUserStatus: 'idle',
  deletingUserStatus: 'idle',
  updatingUsers: [],
  updatingUsersError: [],
  userFormsOpened: [],
  companyInfoFromDB: {},
  companyInfoUpdateStatus: 'idle',
  accountCommentingRoles: [],
  commentingRolesRequestStatus: 'idle',
  isCommentingEmailExist: [],
  addingCommentingRoleError: null,
  addingCommentingRoleStatus: 'idle',
  updatingCommentingRoles: [],
  updatingCommentingRolesError: [],
  roleFormsOpened: [],
  historicalDownloadStatus: 'idle',
  paymentInfo: null,
  paymentInfoRequestStatus: 'idle',
  paymentInfoReceived: false,
  payments: { paypal: null, check: null, wireTransfer: null, ach: null },
  paymentsRequestStatus: { paypal: 'idle', check: 'idle', wireTransfer: 'idle', ach: 'idle' },
  paymentTerm: { current: null, updating: null },
  approvedAccountProducts: [],
  rejectedAccountProducts: [],
  pendingAccountProducts: [],

  accountHasCommentingV2: false,
  accountHasCommentingV1: false
};


export const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    updateCachedCompanyInfo: (state, action) => {
      const { field, value } = action.payload
      state.cachedCompanyInfo[field] = value
    },
    cacheInvoiceBase64: (state, action) => {
      const { uuid, pdfBase64 } = action.payload
      state[uuid].pdfBase64 = pdfBase64
    },
    toggleIsOpened: (state, action) => {
      if (state.invoiceShown === action.payload) {
        state.invoiceShown = null
      } else {
        state.invoiceShown = action.payload
      }
    },
    updateUpdateStatus: (state, action) => {
      const { field, status } = action.payload
      state[field] = status
    },
    resetToDB: (state, action) => {
      const { cache, db } = action.payload
      if (state[db]) state[cache] = state[db]
    },
    toggleAccountUserForm: (state, action) => {
      const userUUID = action.payload
      const idx = state.userFormsOpened.indexOf(userUUID);
      idx < 0 ? state.userFormsOpened.push(userUUID) : state.userFormsOpened.splice(idx, 1);
    },
    closeAccountUserForms: (state, action) => {
      state.userFormsOpened.length = 0
    },
    toggleAccountRoleForm: (state, action) => {
      const roleUUID = action.payload
      const idx = state.roleFormsOpened.indexOf(roleUUID);
      idx < 0 ? state.roleFormsOpened.push(roleUUID) : state.roleFormsOpened.splice(idx, 1);
    },
    closeAccountRoleForms: (state, action) => {
      state.roleFormsOpened.length = 0
    },
    resetHistoricalDownloadStatus: (state, action) => {
      state.historicalDownloadStatus = 'idle'
    },
    updateCurrentPaymentTerm: (state, action) => {
      state.paymentTerm.updating = action.payload
    },
    // after approval of products 2.0 accounts
    addProductsV2ToAccount: (state, action) => {
      const product = action.payload
      if(product && ['COMMENTING_V2', 'TRENDING_NOW_V2', 'ARTICLE_SENTIMENT_V2'].includes(product)) {
        state.accountProducts = [...state.accountProducts, product]
        state.approvedAccountProducts = [...state.approvedAccountProducts, product]
        
        state.accountHasCommentingV1 = state.accountProducts.includes('COMMENTING');
        state.accountHasCommentingV2 = state.accountProducts.includes('COMMENTING_V2');
      }
    },
    setSiteProductsToV2: (state, action) => {
      const productsFiltered = state.accountProducts.filter(prod => !(prod === 'COMMENTING' || prod === 'COMMENTING_V2'))
      if (state.accountHasCommentingV2) {
        productsFiltered.push('COMMENTING_V2')
      }
      state.accountProducts = [...productsFiltered]
    },
    setSiteProductsToV1: (state, action) => {
      if (!state.accountHasCommentingV1) return
      const productsFiltered = state.accountProducts.filter(prod => !(prod === 'COMMENTING' || prod === 'COMMENTING_V2'))
      productsFiltered.push('COMMENTING')
      state.accountProducts = [...productsFiltered]
    },
    updateDisplayInvoice: (state, action) => {
      const invoiceId = action.payload
      const targetInvoice = state.invoices.find(invoice => invoice.id === invoiceId);
      targetInvoice.displayInvoice = !targetInvoice.displayInvoice
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchAccountSites.pending, (state, action) => {
        state.sitesRequestStatus = 'pending'
      })
      .addCase(fetchAccountSites.fulfilled, (state, action) => {
        state.sites = action.payload.sites || action.payload
        state.sitesReceived = true
        state.sitesRequestStatus = 'idle'
      })
      .addCase(fetchAccountSitesAndApps.pending, (state, action) => {
        state.sitesAndARequestStatus = 'pending'
      })
      .addCase(fetchAccountSitesAndApps.fulfilled, (state, action) => {
        state.sitesAndApps = action.payload
        // merge {sites:{uuid: url}, apps:{uuid: url}} => {uuid: url}
        for (let key in action.payload.sites) {
          state.sitesAndAppsUUIDs[key] = action.payload.sites[key];
        }
        for (let key in action.payload.apps) {
          state.sitesAndAppsUUIDs[key] = action.payload.apps[key];
        }
        state.sitesAndAppsReceived = true
        state.sitesAndARequestStatus = 'idle'
      })
      .addCase(fetchAccountInfo.pending, (state, action) => {
        state.accountInfoRequestStatus = 'pending'
      })
      .addCase(fetchAccountInfo.fulfilled, (state, action) => {
        const { accountName, businessDomain, accountGrade, isActive, streetAddress, city, region, zipCode,
          sellerType, showNewRevenueMetrics, okoAccount, paymentTerm } = action.payload
        state.accountName = accountName
        state.businessDomain = businessDomain
        state.accountGrade = accountGrade
        state.streetAddress = streetAddress
        state.city = city
        state.region = region
        state.zipCode = zipCode
        state.sellerType = sellerType
        state.accountInfoReceived = true
        state.accountInfoRequestStatus = 'idle'
        state.showNewRevenueMetrics = showNewRevenueMetrics
        state.isOkoAccount = okoAccount
        state.paymentTerm.current = paymentTerm
        const companyInfo = {
          "accountName": accountName,
          "businessDomain": businessDomain,
          "accountGrade": accountGrade,
          "isActive": isActive,
          "sellerType": sellerType,
          "streetAddress": streetAddress,
          "city": city,
          "region": region,
          "zipCode": zipCode
        }
        state.cachedCompanyInfo = companyInfo
        state.companyInfoFromDB = companyInfo
      }
      )
      .addCase(fetchInvoices.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        const results = action.payload.map(invoice => ({
          ...invoice,
          formattedDate: new Date(invoice.date)
            .toLocaleString('en-us', { month: 'short', day: 'numeric', year: 'numeric' }),
          formattedTotal: invoice.currencyCode === 'USD' ? new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
          }).format(invoice.totalAmountDue) : new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
          }).format(invoice.totalAmountDue)
        }))
        state.invoices = results;
        state.invoicesFromDB = results;
        state.isLoading = false;
      })
      .addCase(fetchAccountSettings.pending, (state, action) => {
        state.accountSettingsRequestStatus = 'pending'
      })
      .addCase(fetchAccountSettings.fulfilled, (state, action) => {
        state.accountSettingsRequestStatus = 'idle'
        state.accountSettings = { ...action.payload }
      })
      .addCase(fetchAccountSettings.rejected, (state, action) => {
        state.accountSettingsRequestStatus = 'error'
      })
      .addCase(updateAccountSettings.pending, (state, action) => {
        state.accountSettingsRequestStatus = 'pending'
      })
      .addCase(updateAccountSettings.fulfilled, (state, action) => {
        const { settings } = action.meta.arg
        state.accountSettings = { ...settings };
        state.accountSettingsRequestStatus = 'success'
      })
      .addCase(updateAccountSettings.rejected, (state, action) => {
        state.accountSettingsRequestStatus = 'error'
      })
      .addCase(updateApiAccess.pending, (state, action) => {
        state.accountSettingsRequestStatus = 'pending'
      })
      .addCase(updateApiAccess.fulfilled, (state, action) => {
        const { settings } = action.meta.arg
        state.accountSettings = { ...settings };
        state.accountSettingsRequestStatus = 'success'
      })
      .addCase(updateApiAccess.rejected, (state, action) => {
        state.accountSettingsRequestStatus = 'error'
      })
      .addCase(fetchAccountUsers.pending, (state, action) => {
        state.accountUsersRequestStatus = 'pending'
      })
      .addCase(fetchAccountUsers.fulfilled, (state, action) => {
        state.accountUsersRequestStatus = 'idle'
        state.accountUsers = [...action.payload]
      })
      // addUser
      .addCase(addUser.pending, (state, action) => {
        state.addingUserError = null
        state.addingUserStatus = 'pending'
      })
      .addCase(addUser.fulfilled, (state, action) => {
        state.addingUserStatus = 'idle'
        state.addingUserError = null
        state.accountUsers.push(action.payload)
      })
      .addCase(addUser.rejected, (state, action) => {
        state.addingUserError = action.payload
        state.addingUserStatus = 'error'
      })
      // updateUser
      .addCase(updateUser.pending, (state, action) => {
        const { userUUID } = action.meta.arg;
        const currentlyUpdating = state.updatingUsers.indexOf(userUUID);
        currentlyUpdating < 0 ? state.updatingUsers.push(userUUID) : state.updatingUsers.splice(currentlyUpdating, 1);

        // remove update error of user
        const currentUserErrorIndex = state.updatingUsersError.map(usersErr => usersErr.userUUID).indexOf(userUUID);
        if (currentUserErrorIndex >= 0) state.updatingUsersError.splice(currentUserErrorIndex, 1)
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        const { userUUID } = action.payload;
        const currentlyUpdating = state.updatingUsers.indexOf(userUUID);
        state.updatingUsers.splice(currentlyUpdating, 1)

        // update account users state
        const updatedUserIndex = [...state.accountUsers].map(user => user.userUUID).indexOf(userUUID)
        let updatedAccountUsers = [...state.accountUsers];
        updatedAccountUsers[updatedUserIndex] = action.payload;
        state.accountUsers = [...updatedAccountUsers]

        // remove update error of user
        const currentUserErrorIndex = state.updatingUsersError.map(usersErr => usersErr.userUUID).indexOf(userUUID);
        if (currentUserErrorIndex >= 0) state.updatingUsersError.splice(currentUserErrorIndex, 1)

      })
      .addCase(updateUser.rejected, (state, action) => {
        const { userUUID } = action.meta.arg;
        const error = action.payload;

        const currentlyUpdating = state.updatingUsers.indexOf(userUUID);
        state.updatingUsers.splice(currentlyUpdating, 1)

        // add update error of user
        const currentUserErrorIndex = state.updatingUsersError.map(usersErr => usersErr.userUUID).indexOf(userUUID);
        if (currentUserErrorIndex < 0) state.updatingUsersError.push({ userUUID, error })
      })
      // switch activation
      .addCase(setCommentingAccountStatus.fulfilled, (state, action) => {
        const { userUUID, deactivated } = action.payload;
        const currentlyUpdating = state.updatingUsers.indexOf(userUUID);
        state.updatingUsers.splice(currentlyUpdating, 1)

        // update account users state
        const updatedUserIndex = [...state.accountUsers].map(user => user.userUUID).indexOf(userUUID)
        let updatedAccountUsers = [...state.accountUsers];
        updatedAccountUsers[updatedUserIndex] = {
          ...updatedAccountUsers[updatedUserIndex],
          commentingUser: {
            ...updatedAccountUsers[updatedUserIndex],
            deactivated
          }
        }
        state.accountUsers = [...updatedAccountUsers]

        // remove update error of user
        const currentUserErrorIndex = state.updatingUsersError.map(usersErr => usersErr.userUUID).indexOf(userUUID);
        if (currentUserErrorIndex >= 0) state.updatingUsersError.splice(currentUserErrorIndex, 1)
      })
      // inviteUser
      .addCase(inviteUser.pending, (state, action) => {
        state.sendingInvite = true
      })
      .addCase(inviteUser.fulfilled, (state, action) => {
        state.sendingInvite = false
        const { userUUID } = action.payload;
        const updatedUserIndex = state.accountUsers.map(user => user.userUUID).indexOf(userUUID)
        state.accountUsers[updatedUserIndex] = { ...state.accountUsers[updatedUserIndex], userStatus: 'INVITE_SENT' }
      })
      .addCase(inviteUser.rejected, (state, action) => {
        state.sendingInvite = false
      })
      // resendInvite
      .addCase(resendInvite.pending, (state, action) => {
        const userUUID = action.meta.arg;
        const currentlySending = state.resendingInvite.indexOf(userUUID);
        currentlySending < 0 ? state.resendingInvite.push(userUUID) : state.resendingInvite.splice(currentlySending, 1);
      })
      .addCase(resendInvite.fulfilled, (state, action) => {
        const { userUUID } = action.payload;
        const currentlySending = state.resendingInvite.indexOf(userUUID);
        state.resendingInvite.splice(currentlySending, 1)
      })
      .addCase(resendInvite.rejected, (state, action) => {
        const { userUUID } = action.payload;
        const currentlySending = state.resendingInvite.indexOf(userUUID);
        state.resendingInvite.splice(currentlySending, 1)
      })
      // deleteUser
      .addCase(deleteUser.pending, (state, action) => {
        state.deletingUserStatus = 'pending'
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        const userUUID = action.payload;
        const deletedUserIndex = state.accountUsers.map(user => user.userUUID).indexOf(userUUID)
        state.accountUsers.splice(deletedUserIndex, 1)
        state.deletingUserStatus = 'idle'
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.deletingUserStatus = 'error'
      })
      // fetchAccountCommentingRoles
      .addCase(fetchAccountCommentingRoles.pending, (state, action) => {
        state.accountCommentingRoles = action.payload
      })
      .addCase(fetchAccountCommentingRoles.fulfilled, (state, action) => {
        state.accountCommentingRoles = action.payload
      })
      .addCase(fetchAccountCommentingRoles.rejected, (state, action) => {
        state.accountCommentingRoles = []
      })
      // createCustomCommentingRole
      .addCase(createCustomCommentingRole.pending, (state, action) => {
        state.addingCommentingRoleError = null
        state.addingCommentingRoleStatus = 'pending'
      })
      .addCase(createCustomCommentingRole.fulfilled, (state, action) => {
        state.addingCommentingRoleStatus = 'idle'
        state.addingCommentingRoleError = null
        state.accountCommentingRoles.push(action.payload)
      })
      .addCase(createCustomCommentingRole.rejected, (state, action) => {
        state.addingCommentingRoleError = action.payload
        state.addingCommentingRoleStatus = 'error'
      })
      // updateCustomCommentingRole
      .addCase(updateCustomCommentingRole.pending, (state, action) => {
        const { roleUUID: uuid } = action.meta.arg;
        const currentlyUpdating = state.updatingCommentingRoles.indexOf(uuid);
        currentlyUpdating < 0 ? state.updatingCommentingRoles.push(uuid) : state.updatingCommentingRoles.splice(currentlyUpdating, 1);

        // remove update error of role
        const currentRoleErrorIndex = state.updatingCommentingRolesError.map(rolesErr => rolesErr.uuid).indexOf(uuid);
        if (currentRoleErrorIndex >= 0) state.updatingCommentingRolesError.splice(currentRoleErrorIndex, 1)
      })
      .addCase(updateCustomCommentingRole.fulfilled, (state, action) => {
        const { uuid } = action.payload;
        const currentlyUpdating = state.updatingCommentingRoles.indexOf(uuid);
        state.updatingCommentingRoles.splice(currentlyUpdating, 1)

        // update account roles state
        const updatedRoleIndex = [...state.accountCommentingRoles].map(role => role.uuid).indexOf(uuid)
        let updatedAccountRoles = [...state.accountCommentingRoles];
        updatedAccountRoles[updatedRoleIndex] = action.payload;
        state.accountCommentingRoles = [...updatedAccountRoles]

        // remove update error of role
        const currentRoleErrorIndex = state.updatingCommentingRolesError.map(rolesErr => rolesErr.uuid).indexOf(uuid);
        if (currentRoleErrorIndex >= 0) state.updatingCommentingRolesError.splice(currentRoleErrorIndex, 1)

      })
      .addCase(updateCustomCommentingRole.rejected, (state, action) => {
        const { uuid } = action.meta.arg;
        const error = action.payload;

        const currentlyUpdating = state.updatingCommentingRoles.indexOf(uuid);
        state.updatingCommentingRoles.splice(currentlyUpdating, 1)

        // add update error of role
        const currentRoleErrorIndex = state.updatingCommentingRolesError.map(rolesErr => rolesErr.uuid).indexOf(uuid);
        if (currentRoleErrorIndex < 0) state.updatingCommentingRolesError.push({ uuid, error })
      })
      // fetchAccountProducts
      .addCase(fetchAccountProducts.pending, (state, action) => {
        state.isLoading = true
        state.accountProductsReceived = false
        state.accountProductRequestStatus = 'pending'
      })
      .addCase(fetchAccountProducts.fulfilled, (state, action) => {
        // TODO: Remove this once BE is integrated. 
        state.accountProducts = action.payload;
        state.isLoading = false
        state.accountProductsReceived = true
        state.accountProductRequestStatus = 'success'

        // Needed to properly switch products for migrated / non-migrated sites to Commenting v2 within same account. Can be removed after migration is done
        state.accountHasCommentingV1 = action.payload.includes('COMMENTING');
        state.accountHasCommentingV2 = action.payload.includes('COMMENTING_V2');
      })
      .addCase(fetchAccountProducts.rejected, (state, action) => {
        state.isLoading = false
        state.accountProductsReceived = true
        state.accountProductRequestStatus = 'failed'
      })

      .addCase(fetchApprovedAccountProducts.fulfilled, (state, action) => {
        // TODO: Remove this once BE is integrated. 
        state.approvedAccountProducts = action.payload
        state.accountProducts = action.payload
      })

      .addCase(fetchPendingAccountProducts.fulfilled, (state, action) => {
        state.pendingAccountProducts = action.payload
      })

      .addCase(fetchRejectedAccountProducts.fulfilled, (state, action) => {
        state.rejectedAccountProducts = action.payload
      })

      .addCase(fetchInvoice.pending, (state, action) => {
        const { date } = action.meta.arg
        state[date] = { status: 'pending' }
      })
      .addCase(fetchInvoice.fulfilled, (state, action) => {
        const { date } = action.meta.arg
        state[date].blob = action.payload
        state[date].status = 'success'
      })
      .addCase(updateAccountDetails.pending, (state, action) => {
        state.companyInfoUpdateStatus = 'pending'
      })
      .addCase(updateAccountDetails.rejected, (state, action) => {
        state.cachedCompanyInfo = state.companyInfoFromDB
        state.companyInfoUpdateStatus = 'failed'
      })
      .addCase(updateAccountDetails.fulfilled, (state, action) => {
        state.cachedCompanyInfo = action.payload
        state.companyInfoFromDB = action.payload
        state.paymentTerm.current = action.payload.paymentTerm
        state.paymentTerm.updating = null
        state.companyInfoUpdateStatus = 'success'
      })
      .addCase(fetchHistoricalData.pending, (state, action) => {
        state.historicalDownloadStatus = 'pending'
      })
      .addCase(fetchHistoricalData.rejected, (state, action) => {
        state.historicalDownloadStatus = 'error'
      })
      .addCase(fetchHistoricalData.fulfilled, (state, action) => {
        const blob = URL.createObjectURL(action.payload)
        const link = document.createElement('a')
        link.href = blob
        link.download = 'historical_report.csv'
        link.click()
        state.historicalDownloadStatus = 'success'
      })
      .addCase(fetchPaymentInformation.pending, (state, action) => {
        state.paymentInfoRequestStatus = 'pending'
        state.paymentInfo = null
      })
      .addCase(fetchPaymentInformation.fulfilled, (state, action) => {
        state.paymentInfoRequestStatus = 'success'
        state.paymentInfo = action.payload
      })
      .addCase(fetchPaymentInformation.rejected, (state, action) => {
        state.paymentInfoRequestStatus = 'error'
        state.paymentInfo = null
      })
      .addCase(fetchPaypalInformation.pending, (state, action) => {
        state.paymentsRequestStatus.paypal = 'pending'
        state.payments.paypal = null
      })
      .addCase(fetchPaypalInformation.fulfilled, (state, action) => {
        state.paymentsRequestStatus.paypal = 'success'
        state.payments.paypal = action.payload
      })
      .addCase(fetchPaypalInformation.rejected, (state, action) => {
        state.paymentsRequestStatus.paypal = 'error'
        state.payments.paypal = null
      })
      .addCase(fetchACHInformation.pending, (state, action) => {
        state.paymentsRequestStatus.ach = 'pending'
        state.payments.ach = null
      })
      .addCase(fetchACHInformation.fulfilled, (state, action) => {
        state.paymentsRequestStatus.ach = 'success'
        state.payments.ach = action.payload
      })
      .addCase(fetchACHInformation.rejected, (state, action) => {
        state.paymentsRequestStatus.ach = 'error'
        state.payments.ach = null
      })
      .addCase(fetchCheckInformation.pending, (state, action) => {
        state.paymentsRequestStatus.check = 'pending'
        state.payments.check = null
      })
      .addCase(fetchCheckInformation.fulfilled, (state, action) => {
        state.paymentsRequestStatus.check = 'success'
        state.payments.check = action.payload
      })
      .addCase(fetchCheckInformation.rejected, (state, action) => {
        state.paymentsRequestStatus.check = 'error'
        state.payments.check = null
      })
      .addCase(fetchWireTransferInformation.pending, (state, action) => {
        state.paymentsRequestStatus.wireTransfer = 'pending'
        state.payments.wireTransfer = null
      })
      .addCase(fetchWireTransferInformation.fulfilled, (state, action) => {
        state.paymentsRequestStatus.wireTransfer = 'success'
        state.payments.wireTransfer = action.payload
      })
      .addCase(fetchWireTransferInformation.rejected, (state, action) => {
        state.paymentsRequestStatus.wireTransfer = 'error'
        state.payments.wireTransfer = null
      })
      .addCase(saveDisplayInvoice.pending, (state, action) => {
        state.invoiceUpdateStatus = 'pending'
      })
      .addCase(saveDisplayInvoice.fulfilled, (state, action) => {
        state.invoicesFromDB = state.invoices
        state.invoiceUpdateStatus = 'success'
      })
      .addCase(saveDisplayInvoice.rejected, (state, action) => {
        state.invoicesFromDB = state.invoices
        state.invoiceUpdateStatus = 'error'
      })

  }
});

// export const { setUserIP, setSessionData, storeSessionData } = accountSlice.actions;
export const {
  updateCachedCompanyInfo,
  cacheInvoiceBase64,
  toggleIsOpened,
  updateUpdateStatus,
  resetToDB,
  toggleAccountUserForm,
  closeAccountUserForms,
  toggleAccountRoleForm,
  closeAccountRoleForms,
  resetHistoricalDownloadStatus,
  updateCurrentPaymentTerm,
  setSiteProductsToV2,
  setSiteProductsToV1,
  updateDisplayInvoice,
  addProductsV2ToAccount
} = accountSlice.actions
export default accountSlice.reducer;
